import { tools, type ToolRegistry } from "idfweb-tools";

import { parsePattern } from "./monitor-script.ts";

/**
 * What happened in the page that a guide can wait for: the guide conditions.
 * The page logs each event with its time, so a wait can also take one that
 * happened shortly before it started.
 */
export type PageEvent =
    /** A workbench command started: from the palette, a key, a toolbar button or a link. */
    | { kind: "command"; id: string }
    /** A build ended, the person's or an agent's. */
    | { kind: "build"; ok: boolean; elf?: string; message?: string; target?: string }
    | { kind: "flash"; ok: boolean; board: string; error?: string }
    /** The chip target changed: in the dropdown, to a detected board's chip, or by set_target. */
    | { kind: "target"; target: string }
    /** A board became connected and ready. */
    | { kind: "board"; label: string }
    /** The quick input opened; ">" is the command palette, "" Go to File. */
    | { kind: "palette"; prefix: string }
    /** An editor wrote a file, by a save or an auto-save. */
    | { kind: "saved"; path: string }
    /** A line of the board's output, without its line end and color escapes. */
    | { kind: "serial"; line: string };

export type EventKind = PageEvent["kind"];

/** Does not compile while the kinds above and wait_for's events (`WAIT_EVENTS`) differ. */
const sameKinds: [EventKind, tools.WaitEvent] extends [tools.WaitEvent, EventKind] ? true : never = true;
void sameKinds;

export const EVENT_KINDS = Object.keys(tools.WAIT_EVENTS) as EventKind[];

/** An event kind, and what the event's command, prefix, path or line must be. */
export interface Condition {
    kind: EventKind;
    id?: string;
    prefix?: string;
    path?: string;
    pattern?: RegExp;
}

export interface Logged {
    at: number;
    event: PageEvent;
}

/** How many events the log keeps, apart from serial lines, and how many serial lines. */
const EVENTS_KEPT = 200;
const LINES_KEPT = 1000;

const ANSI_ESCAPE = /\x1b\[[0-9;]*[A-Za-z]/g;

export function matches(condition: Condition, event: PageEvent): boolean {
    if (event.kind !== condition.kind) return false;
    switch (event.kind) {
        case "command":
            return condition.id === undefined || event.id === condition.id;
        case "palette":
            return condition.prefix === undefined || event.prefix === condition.prefix;
        case "saved":
            return condition.path === undefined || event.path === condition.path;
        case "serial":
            return condition.pattern === undefined || condition.pattern.test(event.line);
        default:
            return true;
    }
}

/**
 * The page's log of events, and waits on it. An event that a wait returned
 * is not returned again, so two waits in a row for a save need two saves.
 */
export class PageEvents {
    readonly #now: () => number;
    readonly #events: (Logged & { taken?: boolean })[] = [];
    readonly #lines: (Logged & { taken?: boolean })[] = [];
    readonly #listeners = new Set<(logged: Logged & { taken?: boolean }) => void>();
    #partial = "";

    constructor(now: () => number = Date.now) {
        this.#now = now;
    }

    emit(event: PageEvent): void {
        const logged = { at: this.#now(), event };
        const log = event.kind === "serial" ? this.#lines : this.#events;
        log.push(logged);
        if (log.length > (event.kind === "serial" ? LINES_KEPT : EVENTS_KEPT)) log.shift();
        for (const listener of [...this.#listeners]) listener(logged);
    }

    /** Output from the board, as it arrives; each line it completes is an event. */
    serial(text: string): void {
        const lines = (this.#partial + text.replace(ANSI_ESCAPE, "")).split("\n");
        this.#partial = lines.pop()!;
        for (const line of lines) this.emit({ kind: "serial", line: line.replace(/\r$/, "") });
    }

    /**
     * The first event at or after `since` that meets the condition, or the
     * next one within `timeoutMs`; undefined if none came.
     */
    wait(condition: Condition, since: number, timeoutMs: number): Promise<Logged | undefined> {
        const log = condition.kind === "serial" ? this.#lines : this.#events;
        const found = log.find((each) => !each.taken && each.at >= since && matches(condition, each.event));
        if (found !== undefined) {
            found.taken = true;
            return Promise.resolve({ at: found.at, event: found.event });
        }
        return new Promise((resolve) => {
            const listener = (logged: Logged & { taken?: boolean }) => {
                if (logged.taken || !matches(condition, logged.event)) return;
                logged.taken = true;
                done({ at: logged.at, event: logged.event });
            };
            const done = (logged: Logged | undefined) => {
                clearTimeout(timer);
                this.#listeners.delete(listener);
                resolve(logged);
            };
            const timer = setTimeout(() => done(undefined), timeoutMs);
            this.#listeners.add(listener);
        });
    }
}

const WAIT_DEFAULT_S = 300;
const WAIT_MAX_S = 1800;
/** How often a wait reports that it is still waiting, so a client that counts silence as a timeout keeps the call. */
const WAIT_PROGRESS_MS = 20_000;

const WAITED_FOR: Record<EventKind, string> = {
    build: "the end of a build",
    flash: "the end of a flash",
    target: "a change of the chip target",
    board: "a board to connect",
    command: "a command",
    palette: "the quick input to open",
    saved: "a save",
    serial: "a matching line of serial output",
};

/** `wait_for`: the agent's side of the guide conditions. */
export function addWaitTool(
    registry: ToolRegistry,
    deps: { events: PageEvents; board: () => { label: string; ready: boolean } | undefined },
): void {
    registry.add(tools.waitFor, async ({ event, id, prefix, path, pattern, timeout_seconds }, context) => {
        const kind = EVENT_KINDS.find((each) => each === event);
        if (kind === undefined) return { text: `event must be one of ${EVENT_KINDS.join(", ")}.`, isError: true };
        if (kind === "serial" && pattern === undefined) return { text: "serial needs a pattern.", isError: true };
        const board = deps.board();
        if (kind === "board" && board?.ready) return { text: `${board.label} is connected already.` };
        const seconds = Math.min(Math.max(timeout_seconds ?? WAIT_DEFAULT_S, 1), WAIT_MAX_S);
        const condition: Condition = {
            kind,
            id,
            prefix,
            path,
            pattern: pattern === undefined ? undefined : parsePattern(pattern),
        };
        const started = Date.now();
        const what = WAITED_FOR[kind];
        const ticker = setInterval(
            () => context.progress(`still waiting for ${what}, ${Math.round((Date.now() - started) / 1000)} s`),
            WAIT_PROGRESS_MS,
        );
        try {
            const logged = await deps.events.wait(condition, context.previousCall ?? started, seconds * 1000);
            if (logged === undefined) return { text: `Waited ${seconds} s for ${what}; none came.`, isError: true };
            const before = Math.round((started - logged.at) / 1000);
            const when =
                logged.at >= started
                    ? `, after ${Math.round((logged.at - started) / 1000)} s`
                    : before === 0
                      ? ", just before this call"
                      : `, ${before} s before this call`;
            return { text: `${describeEvent(logged.event)}${when}.` };
        } finally {
            clearInterval(ticker);
        }
    });
}

function describeEvent(event: PageEvent): string {
    switch (event.kind) {
        case "build": {
            const chip = event.target === undefined ? "" : ` for ${event.target}`;
            return event.ok
                ? `The build${chip} succeeded: ${event.elf ?? ""}`.trimEnd()
                : `The build${chip} failed: ${event.message ?? "see the build output"}`;
        }
        case "flash":
            return event.ok
                ? `${event.board} was flashed and reset`
                : `The flash of ${event.board} failed: ${event.error}`;
        case "target":
            return `The chip target changed to ${event.target}`;
        case "board":
            return `${event.label} connected`;
        case "command":
            return `The command ${event.id} ran`;
        case "palette":
            if (event.prefix === ">") return "The command palette opened";
            if (event.prefix === "") return "Go to File opened";
            return `The quick input opened with the prefix "${event.prefix}"`;
        case "saved":
            return `${event.path} was saved`;
        case "serial":
            return `The board printed: ${event.line}`;
    }
}
